import {autoRegister, autoRegisterAs, resolve} from "../../container";
import {RequestSugarFactory} from "../common/requestSugar";
import {promiseFromJsonResponse, promiseFromUndefinedResponse, promiseFromVoidResponse} from "../../common/utils/promises";
import {SecuredRequestExecutor} from "../../module/awswaf/securedRequestExecutor";
import type {SubmitRequest, SubmitResponse, SubmitService} from "../form/formService";
import {HTTP_STATUS} from "../common/httpStatus";
import type {Maybe} from "../../bootstrap/common/types";

@autoRegisterAs("negativeReasonsFeedbackService")
export class NegativeReasonsFeedbackService implements SubmitService {
    public constructor(
        private feedbackService: FeedbackService = resolve(FeedbackService)
    ) {
    }

    public async submit(request: SubmitRequest): Promise<SubmitResponse> {
        const reasons = request.formData.reasons.options_checked_ids as string;
        const feedbackId = request.formData.reasons.feedback_id as string;
        const parsedReasons = JSON.parse(reasons) as string[];
        await this.feedbackService.updatePageFeedbackReasons(feedbackId, parsedReasons);
        return {success: true};
    }
}

@autoRegisterAs("positiveNewsletterRegistrationService")
export class PositiveNewsletterRegistrationService implements SubmitService {
    public constructor(
        private feedbackService: FeedbackService = resolve(FeedbackService)
    ) {
    }

    public async submit(request: SubmitRequest): Promise<SubmitResponse> {
        const errorId = await this.feedbackService.registerForNewsletter(request.formData.newsletter.email, request.lang);
        return {success: !errorId, errorId};
    }
}

@autoRegister()
export class FeedbackService {

    private basePath: string = "/enbw-eop/eop-app-feedback/rest/stable";

    public constructor(private securedRequestExecutor: SecuredRequestExecutor = resolve(SecuredRequestExecutor)) {
    }

    public async postPageFeedback(url: string, choice: string): Promise<PageFeedbackCreationResponse> {
        const postRequest = (): Promise<Response> => fetch(`${this.basePath}/feedback`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json;charset=UTF-8",
                "EOP-Sugar": RequestSugarFactory.produce()
            },
            body: JSON.stringify({url: url, choice: choice})
        });
        const response = await this.securedRequestExecutor.execute(postRequest);
        return promiseFromJsonResponse(response);
    }

    public async updatePageFeedbackReasons(id: string, reasons: string[]): Promise<void> {
        const request = (): Promise<Response> => fetch(`${this.basePath}/feedback/${id}/reasons`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json;charset=UTF-8",
                "EOP-Sugar": RequestSugarFactory.produce()
            },
            body: JSON.stringify({reasons})
        });
        const response = await this.securedRequestExecutor.execute(request);
        return promiseFromVoidResponse(response);
    }

    public async registerForNewsletter(email: string, language: string): Promise<Maybe<string>> {
        const request = (): Promise<Response> => fetch(`${this.basePath}/newsletter-registration/${language}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json;charset=UTF-8",
                "EOP-Sugar": RequestSugarFactory.produce()
            },
            body: JSON.stringify({email})
        });
        const response = await this.securedRequestExecutor.execute(request);
        return REGISTER_FOR_NEWSLETTER_HTTP_CODES.get(response.status) || promiseFromUndefinedResponse(response);
    }
}

const REGISTER_FOR_NEWSLETTER_HTTP_CODES: Map<number, string> = new Map([
    [HTTP_STATUS.Conflict, "active_recipient"]
]);

export type PageFeedbackCreationResponse = {
    id: string;
}